import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';

import { CellSelection } from '../cell-selection';
import { TableMap } from '../table-map';
import type { MoveOptions } from '../types';
import { determineTableHeaderStateFromTableNode } from './analyse-table';
import { cloneTr } from './clone-tr';
import { findTable } from './find-table';
import { getSelectionRangeInRow } from './get-selection-range-in-row';
import { isValidReorder } from './is-valid-reorder';
import { normalizeDirection } from './normalize-direction';
import type { TableNodeCache } from './table-node-types';
import { tableNodeTypes } from './table-node-types';

// :: (originRowIndex: number, targetRowIndex: targetColumnIndex, options?: MovementOptions) → (tr: Transaction) → Transaction
// Returns a new transaction that moves the origin row to the target index;
//
// by default "tryToFit" is false, that means if you try to move a row to a place
// where we will need to split a row with merged cells it'll throw an exception, for example:
//
// ```
//      ____________________________
//     |      |      |             |
//  0  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
//     |      |             |      |
//  1  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  2  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
// ```
//
// if you try to move the row 0 to the row index 1 with tryToFit false,
// it'll throw an exception since you can't split the row 1;
// but if "tryToFit" is true, it'll move the row using the current direction.
//
// We defined current direction using the target and origin values
// if the origin is greater than the target, that means the course is `bottom-to-top`,
// so the `tryToFit` logic will use this direction to determine
// if we should move the column to the right or the left.
//
// for example, if you call the function using `moveRow(0, 1, { tryToFit: true })`
// the result will be:
// ```
//      ____________________________
//     |      |             |      |
//  0  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  1  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
//     |      |      |             |
//  2  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
// ```
//
// since we could put the row zero on index one,
// we pushed to the best place to fit the row index 0,
// in this case, row index 2.
//
//
// -------- HOW TO OVERRIDE DIRECTION --------
//
// If you set "tryToFit" to "true", it will try to figure out the best direction
// place to fit using the origin and target index, for example:
//
//
// ```
//      ____________________________
//     |      |      |             |
//  0  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
//     |      |             |      |
//  1  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  2  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
//     |      |             |      |
//  3  |  A4  |     B4      |      |
//     |______|______ ______|      |
//     |      |      |      |  D2  |
//  4  |  A5  |  B5  |  C3  |      |
//     |______|______|______|______|
// ```
//
//
// If you try to move the row 0 to row index 4 with "tryToFit" enabled, by default,
// the code will put it on after the merged rows,
// but you can override it using the "direction" option.
//
// -1: Always put the origin before the target
// ```
//      ____________________________
//     |      |             |      |
//  0  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  1  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
//     |      |      |             |
//  2  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
//     |      |             |      |
//  3  |  A4  |     B4      |      |
//     |______|______ ______|      |
//     |      |      |      |  D2  |
//  4  |  A5  |  B5  |  C3  |      |
//     |______|______|______|______|
// ```
//
//  0: Automatically decide the best place to fit
// ```
//      ____________________________
//     |      |             |      |
//  0  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  1  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
//     |      |             |      |
//  2  |  A4  |     B4      |      |
//     |______|______ ______|      |
//     |      |      |      |  D2  |
//  3  |  A5  |  B5  |  C3  |      |
//     |______|______|______|______|
//     |      |      |             |
//  4  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
// ```
//
//  1: Always put the origin after the target
// ```
//      ____________________________
//     |      |             |      |
//  0  |  A2  |     B2      |      |
//     |______|______ ______|      |
//     |      |      |      |  D1  |
//  1  |  A3  |  B3  |  C2  |      |
//     |______|______|______|______|
//     |      |             |      |
//  2  |  A4  |     B4      |      |
//     |______|______ ______|      |
//     |      |      |      |  D2  |
//  3  |  A5  |  B5  |  C3  |      |
//     |______|______|______|______|
//     |      |      |             |
//  4  |  A1  |  B1  |     C1      |
//     |______|______|______ ______|
// ```
//
// ```javascript
// dispatch(
//   moveRow(x, y, options)(state.tr)
// );
// ```
export const moveRow =
	(
		state: EditorState,
		originRowIndex: number | number[],
		targetRowIndex: number,
		options: MoveOptions = {
			tryToFit: false,
			direction: 0,
			selectAfterMove: false,
		},
	) =>
	(tr: Transaction): Transaction => {
		const table = findTable(tr.selection);
		if (!table) {
			return tr;
		}

		// normalize the origin index to an array since move row support moving both a single & multiple rows in a single action.
		if (!Array.isArray(originRowIndex)) {
			originRowIndex = [originRowIndex];
		}

		if (originRowIndex.includes(targetRowIndex)) {
			return tr;
		}

		const tableMap = TableMap.get(table.node);
		const [originMin, originMax] = originRowIndex.reduce(
			([min, max], cur) => [Math.min(min, cur), Math.max(max, cur)],
			[Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER],
		);

		const originalRowRanges = getSelectionRangeInRow(originMin, originMax)(tr);
		const targetRowRanges = getSelectionRangeInRow(targetRowIndex)(tr);
		const indexesOriginRow = originalRowRanges?.indexes || [];
		const indexesTargetRow = targetRowRanges?.indexes || [];

		if (
			originMin < 0 ||
			originMin === Number.MAX_SAFE_INTEGER ||
			originMax >= tableMap.height ||
			originMax === Number.MIN_SAFE_INTEGER ||
			// The target index cannot be within the origin bounds
			(targetRowIndex >= originMin && targetRowIndex <= originMax)
		) {
			return tr;
		}

		if (!options.tryToFit && indexesTargetRow.length > 1) {
			isValidReorder(originMin, targetRowIndex, indexesTargetRow, 'row');
		}

		const types = tableNodeTypes(state.schema);
		const direction = normalizeDirection(originMin, targetRowIndex, options);
		const actualTargetIndex = Math[direction === 'start' ? 'min' : 'max'](...indexesTargetRow);

		const originPositions = indexesOriginRow.map(
			(index) => tableMap.positionAt(index, 0, table.node) + table.pos,
		);

		const originNodes = originPositions.reduce<{ node: PMNode; pos: number }[]>((acc, pos) => {
			const node = tr.doc.nodeAt(tr.mapping.map(pos));
			if (node) {
				return [...acc, { pos, node }];
			}
			return acc;
		}, []);

		const targetPos = tableMap.positionAt(actualTargetIndex, 0, table.node) + table.pos;
		const targetNode = tr.doc.nodeAt(tr.mapping.map(targetPos));

		if (originNodes?.length && targetNode) {
			const newTr = cloneTr(tr);

			const { rowHeaderEnabled, columnHeaderEnabled } = determineTableHeaderStateFromTableNode(
				table.node,
				tableMap,
				types,
			);

			if (rowHeaderEnabled && (originMin === 0 || actualTargetIndex === 0)) {
				// This block is handling the situation where a row is moved in/out of the header position. If the header row option
				// is enabled then;
				// When a row is moved out, the row will be converted to a normal row and the row below it will become the header.
				// When a row is moved in, the old row header needs to be made normal, and the incoming row needs to be made a header.
				// This section only manages what happens to the other row, no the one being moved.
				const nearHeaderPos =
					tableMap.positionAt(originMin === 0 ? originMax + 1 : actualTargetIndex, 0, table.node) +
					table.pos;
				const nearHeaderNode = newTr.doc.nodeAt(newTr.mapping.map(nearHeaderPos));

				if (nearHeaderNode) {
					nearHeaderNode.forEach((node, offset, index) => {
						const start = newTr.mapping.map(nearHeaderPos + 1 + offset);
						newTr.setNodeMarkup(
							start,
							actualTargetIndex !== 0 || (columnHeaderEnabled && index === 0)
								? types.header_cell
								: types.cell,
							node.attrs,
						);
					});
				}
			}

			const insertPos =
				direction === 'end'
					? newTr.mapping.map(targetPos + targetNode.nodeSize, 1)
					: newTr.mapping.map(targetPos, -1);

			newTr.insert(
				insertPos,
				originNodes.map(({ node }, index) =>
					normalizeRowNode(
						node,
						rowHeaderEnabled && actualTargetIndex === 0 && index === 0,
						columnHeaderEnabled,
						types,
					),
				),
			);

			// IMPORTANT: We need to perform the selection between the insert/delete. This is because the delete could cause
			// the current selection to be pushed out of the table resulting in an invalid selection range outside of the table
			if (options.selectAfterMove) {
				const offset = direction === 'end' ? 1 : 0;
				const selectionRange = getSelectionRangeInRow(
					actualTargetIndex + offset,
					actualTargetIndex + offset + originNodes.length - 1,
				)(newTr);

				if (selectionRange) {
					newTr.setSelection(new CellSelection(selectionRange.$anchor, selectionRange.$head));
				}
			}

			originNodes.forEach(({ pos, node }) => {
				newTr.delete(newTr.mapping.map(pos, 1), newTr.mapping.map(pos + node.nodeSize, -1));
			});

			return newTr;
		}

		return tr;
	};

/**
 * This ensures the row node cell type correctly reflect what they should be.
 * @returns A copy of the rowNode
 */
function normalizeRowNode(
	rowNode: PMNode,
	rowHeaderEnabled: boolean,
	columnHeaderEnabled: boolean,
	types: TableNodeCache,
): PMNode {
	const content: PMNode[] = [];
	rowNode.forEach((node: PMNode, _offset: number, index: number) => {
		const newTargetType: NodeType =
			rowHeaderEnabled || (columnHeaderEnabled && index === 0) ? types.header_cell : types.cell;
		content.push(
			node.type !== newTargetType
				? newTargetType.create(node.attrs, node.content, node.marks)
				: node,
		);
	});

	return rowNode.type.create(rowNode.attrs, content, rowNode.marks);
}
